import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Only initialize if we have a valid URL (not the placeholder)
const isValidUrl = supabaseUrl.startsWith('http');

// Super-admin "Ingresar como": while set, PostgREST calls carry the target
// user's token (minted by the impersonation_token RPC), so RLS and every RPC
// scope to that user's agency exactly as they would see it. Auth, storage and
// edge-function calls keep the real session. A request can opt out (e.g. saving
// the super admin's own UI prefs) with `.setHeader(REAL_USER_HEADER, '1')`.
export const REAL_USER_HEADER = 'x-real-user';
let impersonation: { userId: string; token: string; exp: number } | null = null;
let targetUserId: string | null = null;
let refreshing: Promise<void> | null = null;

const impersonationFetch: typeof fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (!url.includes('/rest/v1/')) return fetch(input, init);
    const headers = new Headers(init?.headers);
    const asRealUser = headers.has(REAL_USER_HEADER);
    headers.delete(REAL_USER_HEADER);
    if (impersonation && !asRealUser && !url.includes('/rpc/impersonation_token')) {
        // Read-only backstop: the token acts AS the user, so a write that slips
        // past useReadOnlyGuard (mark-as-read, dismiss…) must not land on their
        // data. Table writes are refused here; RPCs are POST even for reads, so
        // write RPCs stay the guard's job.
        const method = (init?.method ?? 'GET').toUpperCase();
        if (method !== 'GET' && method !== 'HEAD' && !url.includes('/rest/v1/rpc/')) {
            return new Response(JSON.stringify({ code: 'READ_ONLY', message: 'Modo solo lectura (suplantación).' }), {
                status: 403, headers: { 'Content-Type': 'application/json' },
            });
        }
        if (impersonation.exp - Date.now() / 1000 < 60) {
            await (refreshing ??= setImpersonatedUser(impersonation.userId)
                .catch(err => console.error('Impersonation token refresh failed:', err))
                .finally(() => { refreshing = null; }));
        }
        if (impersonation) headers.set('Authorization', `Bearer ${impersonation.token}`);
    }
    return fetch(input, { ...init, headers });
};

export const supabase = isValidUrl
    ? createClient(supabaseUrl, supabaseAnonKey, {
        global: { fetch: impersonationFetch },
        realtime: {
            params: {
                eventsPerSecond: 2,
            },
        },
    })
    : null;

export const getImpersonatedUserId = () => targetUserId;

/** Start (userId) or stop (null) server-side impersonation. Throws if the token can't be minted. */
export async function setImpersonatedUser(userId: string | null): Promise<void> {
    targetUserId = userId;
    if (!userId || !supabase) {
        impersonation = null;
        return;
    }
    const { data, error } = await supabase.rpc('impersonation_token', { p_user_id: userId });
    if (error || !data?.token) throw error ?? new Error('impersonation_token returned no token');
    // A stop/switch while minting wins over this (now stale) token.
    if (targetUserId === userId) impersonation = { userId, token: data.token, exp: data.expires_at };
}

if (!isValidUrl) {
    console.warn('Supabase credentials not configured. App will run in mock mode.');
}
